/**
 * Thin typed wrapper around `customFetch` for the API endpoints we need
 * before `@workspace/api-client-react` is regenerated. After running
 * `pnpm --filter @workspace/api-spec run codegen`, the generated package
 * will export properly typed React Query hooks
 * (`useListProjects`, `useCreateProject`, etc.) and the call sites in
 * `src/hooks/` can be swapped to those.
 *
 * Until then, this is the contract.
 */

import { customFetch } from "./apiClient";

// ---------------------------------------------------------------------------
// Domain types — mirror docs/SPEC.md §4 / openapi.yaml component schemas.
// Keep this list in sync with the API; replace with generated types once
// codegen is rerun.
// ---------------------------------------------------------------------------

export type ProjectStatus = "draft" | "active" | "submitted" | "won" | "lost";
export type SubmissionFormat = "doc" | "presentation" | "both";
export type SectionKind =
  | "prose_ai"
  | "prose_external"
  | "designer_asset"
  | "template"
  | "personnel"
  | "pricing"
  | "attachment"
  | "compliance";
export type RequirementType =
  | "strategy"
  | "proof"
  | "pricing"
  | "operations"
  | "compliance"
  | "creative"
  | "admin";
export type Priority = "high" | "medium" | "low";
export type MatrixItemStatus =
  | "not_started"
  | "in_progress"
  | "draft_ready"
  | "in_review"
  | "complete";
export type SectionStatus = MatrixItemStatus;
export type DocumentStatus = "not_started" | "in_progress" | "complete" | "not_required";
export type QASeverity = "critical" | "warning" | "suggestion";
export type NoteKind =
  | "briefing_call"
  | "vendor_qa"
  | "addendum"
  | "client_call"
  | "internal_note"
  | "other";
export type FileTag = "main_scope" | "addendum" | "qa" | "evaluation" | "reference";
export type FileTagSource = "ai" | "user" | "default";
export type FileOutcome = "won" | "lost" | "submitted" | "draft";
export type OrgRole = "admin" | "finance" | "member";

export interface Note {
  id: string;
  projectId: string;
  kind: NoteKind;
  body: string;
  fileId: string | null;
  feedToAi: boolean;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectFile {
  id: string;
  projectId?: string;
  filename: string;
  contentType: string;
  byteSize: number;
  kind: string;
  tag: FileTag | null;
  tagConfidence: number | null;
  tagSource: FileTagSource | null;
  /** Past-RFP only: did this proposal win/lose/etc. Null for non-past-rfp files. */
  outcome?: FileOutcome | null;
  /** Past-RFP only: optional reference to the original RFP scope file. */
  pairedFileId?: string | null;
  /** Free-form note from the user about this file. */
  agencyNotes?: string;
  status: "pending" | "ready" | "deleted";
  createdAt: string;
}

export interface Project {
  id: string;
  title: string;
  requesterName: string;
  submissionDeadline: string | null;
  status: ProjectStatus;
  /** doc / presentation / both. Null until the bid lead picks. */
  submissionFormat?: SubmissionFormat | null;
  rfpFileId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectListResponse {
  projects: Project[];
}

/**
 * Aggregated per-project state used by the Dashboard's "next step" line.
 * One row per project, in a single round-trip — driven by GET
 * /v1/projects/dashboard-state.
 */
export interface DashboardProjectState {
  projectId: string;
  status: ProjectStatus;
  hasRfpFile: boolean;
  hasRawRfpText: boolean;
  hasSummary: boolean;
  hasFormat: boolean;
  sectionCount: number;
  sectionsComplete: number;
  sectionsMissingProse: number;
  sectionsMissingAsset: number;
  matrixTotal: number;
  deliverableTotal: number;
  deliverableCovered: number;
}

export interface DashboardStateResponse {
  states: DashboardProjectState[];
}

export interface ResponseMatrixItem {
  id: string;
  projectId: string;
  section: string;
  /** Optional sub-clause identifier from the source document ("Item A", "Article 3.1"). */
  clauseRef?: string | null;
  /** What the parser originally wrote into `section`, before any consolidation. */
  originalSection?: string | null;
  requirement: string;
  type: RequirementType;
  priority: Priority;
  ownerUserId: string | null;
  dueDate: string | null;
  status: MatrixItemStatus;
  /** User has marked this requirement as not applicable to this bid. */
  notApplicable: boolean;
  notes: string;
  linkedSectionId: string | null;
  /**
   * @deprecated Use `externalTaskId` + `externalIntegrationId`. Kept for
   * one rollout window so existing UI code doesn't break.
   */
  teamworkTaskId: string | null;
  /** External task id from whichever PM tool this row syncs to. */
  externalTaskId?: string | null;
  /** Which org_pm_integrations row this task lives in. */
  externalIntegrationId?: string | null;
  /** Last time we successfully synced. */
  externalSyncedAt?: string | null;
  position: number;
  /** Null when the AI parser created the row; user UUID when added manually. */
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProposalSection {
  id: string;
  projectId: string;
  /** Optional in the new model — free-floating sections (Executive Summary) have no matrix link. */
  matrixItemId: string | null;
  title: string;
  /**
   * One-paragraph description of what this section covers. Captured from
   * the AI outline rationale and editable by the user. Fed back into draft
   * generation as section context.
   */
  purpose: string;
  /** What kind of section: prose_ai / prose_external / designer_asset / etc. */
  kind: SectionKind;
  /** Document order in the response. */
  position: number;
  ownerUserId: string | null;
  /** For asset-backed sections: file in storage that backs this section. */
  assetFileId: string | null;
  draftText: string;
  sourceInputs: string;
  status: SectionStatus;
  currentVersion: number;
  /**
   * Sensitive sections (default for `pricing` kind) are visible only to
   * admin / finance roles, the section creator/owner, and explicit
   * additional viewers. Members never see sensitive sections in lists or
   * as direct GETs.
   */
  sensitive: boolean;
  /** Per-section override allowlist for sensitive sections. Empty by default. */
  additionalViewerIds: string[];
  /**
   * For `personnel`-kind sections: which team_profiles rows are featured.
   * The Tailor button reads this and runs each profile through the AI
   * tailorer. Empty for any other kind.
   */
  featuredProfileIds: string[];
  /** Section creator (used for sensitive-section visibility). */
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RequiredDocument {
  id: string;
  projectId: string;
  name: string;
  description: string;
  required: boolean;
  ownerUserId: string | null;
  status: DocumentStatus;
  fileId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QAFinding {
  id: string;
  runId: string;
  severity: QASeverity;
  issue: string;
  whyItMatters: string;
  recommendedFix: string;
  linkedScreen: string | null;
  linkedItemId: string | null;
  createdAt: string;
}

export interface RFPSummary {
  deadline: string;
  deliverables: string[];
  submissionFormat: string;
  requiredDocs: string[];
  budget: string;
  scope: string;
  keyRequirements: string[];
  evaluationCriteria: string[];
}

export interface ProjectDetail extends Project {
  rawRfpText: string | null;
  summary: RFPSummary | null;
  matrix: ResponseMatrixItem[];
  sections: ProposalSection[];
  documents: RequiredDocument[];
  qaFindings: QAFinding[];
  files: ProjectFile[];
}

export type ToneOfVoice =
  | "professional"
  | "conversational"
  | "bold"
  | "warm"
  | "technical"
  | "playful";

export interface OrgContext {
  id: string;
  orgId: string;
  agencyDescription: string;
  positioning: string;
  toneOfVoice: ToneOfVoice | null;
  focusAreas: string[];
  differentiators: string;
  additionalNotes: string;
  brandGuideFileId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PastProject {
  id: string;
  title: string;
  requesterName: string;
  status: ProjectStatus;
  submittedAt: string;
}

export interface OrgContextResponse {
  context: OrgContext;
  pastProjects: PastProject[];
  /** Single brand guide file. Kept for backwards compatibility. */
  brandGuide: ProjectFile | null;
  /** Agency reference docs — brand guide, case studies, capability decks, etc. */
  orgDocs?: ProjectFile[];
  /** Past RFP submissions uploaded as files. Each may have outcome + pair + notes. */
  pastRfpFiles?: ProjectFile[];
}

export type LinkFetchStatus = "pending" | "ready" | "failed";

export interface ClientContext {
  id: string;
  orgId: string;
  projectId: string;
  aboutClient: string;
  theirMarket: string;
  competitors: string;
  additionalNotes: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClientLink {
  id: string;
  projectId: string;
  url: string;
  title: string;
  fetchedText: string;
  fetchStatus: LinkFetchStatus;
  fetchError: string | null;
  fetchedAt: string | null;
  createdBy: string;
  createdAt: string;
}

export interface ClientContextResponse {
  context: ClientContext;
  links: ClientLink[];
}

export interface StrategyTheme {
  title: string;
  description: string;
}

export interface Strategy {
  id: string;
  orgId: string;
  projectId: string;
  positioningStatement: string;
  winThemes: StrategyTheme[];
  creativeIdeas: StrategyTheme[];
  risks: StrategyTheme[];
  additionalNotes: string;
  generatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MeResponse {
  user: {
    id: string;
    email: string;
    displayName: string | null;
    role: OrgRole;
  };
  org: { id: string; name: string; plan: "free" | "team" | "enterprise" };
}

export interface OrgUserSummary {
  id: string;
  email: string;
  displayName: string | null;
  role: OrgRole;
}

export interface OrgUsersResponse {
  users: OrgUserSummary[];
}

export type MatrixActivityKind =
  | "created"
  | "status_changed"
  | "owner_changed"
  | "priority_changed"
  | "type_changed"
  | "na_toggled"
  | "notes_updated"
  | "due_date_changed"
  | "linked_to_section"
  | "teamwork_synced";

export interface MatrixItemActivity {
  id: string;
  matrixItemId: string;
  actorUserId: string | null;
  kind: MatrixActivityKind;
  /**
   * Free-form context. Renderer keys off `kind` to read shape-specific fields:
   * - status_changed / priority_changed / type_changed / due_date_changed: { from, to }
   * - owner_changed: { fromUserId, toUserId }
   * - na_toggled: { notApplicable: boolean }
   * - created: { source: "user" | "parser" }
   * - notes_updated: {}
   */
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface MatrixItemActivityResponse {
  activity: MatrixItemActivity[];
}

// ---------------------------------------------------------------------------
// PM tool integrations (Pass 5b)
// ---------------------------------------------------------------------------

export type PmProvider =
  | "teamwork"
  | "asana"
  | "monday"
  | "planner"
  | "trello"
  | "basecamp"
  | "clickup"
  | "jira";

export interface PmIntegration {
  id: string;
  provider: PmProvider;
  providerWorkspaceId: string;
  providerDefaultProjectId: string | null;
  displayName: string;
  enabled: boolean;
  /** True if any credentials are stored. We never expose the values themselves. */
  credentialsConfigured: boolean;
  /** The HMAC secret to paste into the external tool's webhook config. */
  webhookSecret: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PmIntegrationsResponse {
  integrations: PmIntegration[];
  availableProviders: { provider: PmProvider; displayName: string }[];
}

export interface PmIntegrationTestResult {
  ok: boolean;
  matchedExternalUser?: string | null;
  message: string;
}

// ---------------------------------------------------------------------------
// Section consolidation (parse-matrix Step 2 fix)
// ---------------------------------------------------------------------------

export interface ConsolidationProposal {
  target: string;
  sources: string[];
  reason: string;
}

export interface ConsolidationPreviewResponse {
  proposals: ConsolidationProposal[];
  totalSections: number;
}

export interface ConsolidationApplyResult {
  totalUpdated: number;
  summary: Array<{
    target: string;
    sources: string[];
    updated: number;
  }>;
}

// ---------------------------------------------------------------------------
// Drafts: response outline suggestion + section CRUD
// ---------------------------------------------------------------------------

export interface OutlineSuggestion {
  title: string;
  kind: SectionKind;
  /** AI's rationale, surfaced as the editable "purpose" in the modal. */
  rationale: string;
}

/** Shape posted to apply-outline (rationale → purpose). */
export interface OutlineApplySection {
  title: string;
  kind: SectionKind;
  purpose?: string;
}

export interface OutlineSuggestionResponse {
  sections: OutlineSuggestion[];
}

export interface ApplyOutlineResponse {
  sections: ProposalSection[];
}

// ---------------------------------------------------------------------------
// Section ↔ requirement cross-links
// ---------------------------------------------------------------------------

export interface SectionRequirementLink {
  linkId: string;
  matrixItem: ResponseMatrixItem;
}

export interface SectionLinksResponse {
  links: SectionRequirementLink[];
}

export interface ProjectCoverage {
  /** Total requirements parsed for the project. */
  total: number;
  /** Requirements not marked N/A. */
  applicable: number;
  /** Requirements that are deliverable-style (need a section to address). */
  deliverable: number;
  /** Of the deliverable set, how many have at least one linked section. */
  deliverableCovered: number;
  /** IDs of deliverable requirements with no section link — surfaces as warnings. */
  deliverableUncoveredIds: string[];
}

// ---------------------------------------------------------------------------
// Team profiles (Pass 7c) — org-level cast for personnel sections
// ---------------------------------------------------------------------------

export interface TeamProfile {
  id: string;
  orgId: string;
  fullName: string;
  role: string;
  yearsExperience: number | null;
  bio: string;
  expertiseTags: string[];
  canonicalCvText: string;
  certifications: string[];
  linkedinUrl: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface TeamProfilesResponse {
  profiles: TeamProfile[];
}

export interface TeamProfileBody {
  fullName: string;
  role?: string;
  yearsExperience?: number | null;
  bio?: string;
  expertiseTags?: string[];
  canonicalCvText?: string;
  certifications?: string[];
  linkedinUrl?: string | null;
}

// ---------------------------------------------------------------------------
// REST calls
// ---------------------------------------------------------------------------

export const api = {
  me: () => customFetch<MeResponse>("/api/v1/me"),

  listOrgUsers: () => customFetch<OrgUsersResponse>("/api/v1/orgs/me/users"),

  /** Admin-only: change a user's role. */
  updateOrgUser: (userId: string, body: { role?: OrgRole }) =>
    customFetch<OrgUserSummary>(`/api/v1/orgs/me/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  listProjects: (params?: { status?: ProjectStatus; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set("status", params.status);
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    return customFetch<ProjectListResponse>(`/api/v1/projects${qs ? `?${qs}` : ""}`);
  },

  getDashboardState: () => customFetch<DashboardStateResponse>("/api/v1/projects/dashboard-state"),

  createProject: (body: {
    title: string;
    requesterName: string;
    submissionDeadline?: string | null;
    rawText?: string;
    rfpFileId?: string;
    rfpFileIds?: string[];
  }) =>
    customFetch<Project>("/api/v1/projects", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getProject: (id: string) => customFetch<ProjectDetail>(`/api/v1/projects/${id}`),

  updateProject: (
    id: string,
    body: Partial<{
      title: string;
      requesterName: string;
      submissionDeadline: string | null;
      status: ProjectStatus;
      submissionFormat: SubmissionFormat | null;
    }>,
  ) =>
    customFetch<Project>(`/api/v1/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteProject: (id: string) => customFetch<void>(`/api/v1/projects/${id}`, { method: "DELETE" }),

  deleteFile: (projectId: string, fileId: string) =>
    customFetch<void>(`/api/v1/projects/${projectId}/files/${fileId}`, {
      method: "DELETE",
    }),

  updateFileMetadata: (
    fileId: string,
    body: {
      outcome?: FileOutcome | null;
      pairedFileId?: string | null;
      agencyNotes?: string;
    },
  ) =>
    customFetch<ProjectFile>(`/api/v1/files/${fileId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteOrgFile: (fileId: string) =>
    customFetch<void>(`/api/v1/files/${fileId}`, {
      method: "DELETE",
    }),

  deleteSection: (sectionId: string) =>
    customFetch<void>(`/api/v1/sections/${sectionId}`, { method: "DELETE" }),

  listMatrix: (projectId: string) =>
    customFetch<ResponseMatrixItem[]>(`/api/v1/projects/${projectId}/matrix`),

  createMatrixItem: (
    projectId: string,
    body: {
      section: string;
      requirement: string;
      type: RequirementType;
      priority: Priority;
      ownerUserId?: string | null;
      dueDate?: string | null;
      notes?: string;
    },
  ) =>
    customFetch<ResponseMatrixItem>(`/api/v1/projects/${projectId}/matrix`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateMatrixItem: (
    itemId: string,
    body: Partial<Omit<ResponseMatrixItem, "id" | "projectId" | "createdAt" | "updatedAt">>,
  ) =>
    customFetch<ResponseMatrixItem>(`/api/v1/matrix/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteMatrixItem: (itemId: string) =>
    customFetch<void>(`/api/v1/matrix/${itemId}`, { method: "DELETE" }),

  listMatrixItemActivity: (itemId: string) =>
    customFetch<MatrixItemActivityResponse>(`/api/v1/matrix/${itemId}/activity`),

  previewMatrixConsolidation: (projectId: string) =>
    customFetch<ConsolidationPreviewResponse>(
      `/api/v1/projects/${projectId}/matrix/consolidation-preview`,
      { method: "POST" },
    ),

  applyMatrixConsolidation: (
    projectId: string,
    proposals: Pick<ConsolidationProposal, "target" | "sources">[],
  ) =>
    customFetch<ConsolidationApplyResult>(
      `/api/v1/projects/${projectId}/matrix/consolidation-apply`,
      {
        method: "POST",
        body: JSON.stringify({ proposals }),
      },
    ),

  // --- Sections / Drafts --------------------------------------------------

  listSections: (projectId: string) =>
    customFetch<ProposalSection[]>(`/api/v1/projects/${projectId}/sections`),

  getSection: (sectionId: string) => customFetch<ProposalSection>(`/api/v1/sections/${sectionId}`),

  createSection: (
    projectId: string,
    body: {
      title: string;
      kind?: SectionKind;
      matrixItemId?: string | null;
      ownerUserId?: string | null;
      position?: number;
      draftText?: string;
      assetFileId?: string | null;
    },
  ) =>
    customFetch<ProposalSection>(`/api/v1/projects/${projectId}/sections`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateSection: (
    sectionId: string,
    body: Partial<
      Pick<
        ProposalSection,
        | "draftText"
        | "title"
        | "purpose"
        | "kind"
        | "matrixItemId"
        | "ownerUserId"
        | "position"
        | "assetFileId"
        | "status"
        | "sensitive"
        | "additionalViewerIds"
        | "featuredProfileIds"
      >
    >,
  ) =>
    customFetch<ProposalSection>(`/api/v1/sections/${sectionId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  suggestResponseOutline: (projectId: string) =>
    customFetch<OutlineSuggestionResponse>(
      `/api/v1/projects/${projectId}/sections/suggest-outline`,
      { method: "POST" },
    ),

  applyResponseOutline: (projectId: string, sections: OutlineApplySection[]) =>
    customFetch<ApplyOutlineResponse>(`/api/v1/projects/${projectId}/sections/apply-outline`, {
      method: "POST",
      body: JSON.stringify({ sections }),
    }),

  /** Synchronous AI draft generation. Persists draftText on success. */
  generateSectionDraft: (sectionId: string) =>
    customFetch<ProposalSection>(`/api/v1/sections/${sectionId}/draft`, {
      method: "POST",
    }),

  /** Get the requirements (matrix items) linked to this section. */
  listSectionRequirementLinks: (sectionId: string) =>
    customFetch<SectionLinksResponse>(`/api/v1/sections/${sectionId}/requirement-links`),

  /**
   * Replace the section's set of linked requirements with the provided list.
   * Idempotent: same set → no change. Returns the new full set.
   */
  replaceSectionRequirementLinks: (sectionId: string, matrixItemIds: string[]) =>
    customFetch<SectionLinksResponse>(`/api/v1/sections/${sectionId}/requirement-links`, {
      method: "PUT",
      body: JSON.stringify({ matrixItemIds }),
    }),

  /** Per-project coverage snapshot. */
  getProjectCoverage: (projectId: string) =>
    customFetch<ProjectCoverage>(`/api/v1/projects/${projectId}/coverage`),

  /**
   * Personnel sections only: tailor each featured profile against the RFP
   * and stitch the markdown into the section's draftText. Errors with
   * 400 if the section is not personnel kind or no profiles are featured.
   */
  tailorPersonnelSection: (sectionId: string) =>
    customFetch<ProposalSection>(`/api/v1/sections/${sectionId}/tailor-personnel`, {
      method: "POST",
    }),

  // --- Team profiles (Pass 7c) -------------------------------------------

  listTeamProfiles: () => customFetch<TeamProfilesResponse>("/api/v1/orgs/me/team-profiles"),

  getTeamProfile: (profileId: string) =>
    customFetch<TeamProfile>(`/api/v1/orgs/me/team-profiles/${profileId}`),

  createTeamProfile: (body: TeamProfileBody) =>
    customFetch<TeamProfile>("/api/v1/orgs/me/team-profiles", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateTeamProfile: (profileId: string, body: Partial<TeamProfileBody>) =>
    customFetch<TeamProfile>(`/api/v1/orgs/me/team-profiles/${profileId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteTeamProfile: (profileId: string) =>
    customFetch<void>(`/api/v1/orgs/me/team-profiles/${profileId}`, {
      method: "DELETE",
    }),

  // --- PM integrations ----------------------------------------------------

  listPmIntegrations: () => customFetch<PmIntegrationsResponse>("/api/v1/orgs/me/pm-integrations"),

  createPmIntegration: (body: {
    provider: PmProvider;
    providerWorkspaceId: string;
    providerDefaultProjectId?: string | null;
    displayName: string;
    credentials: Record<string, unknown>;
  }) =>
    customFetch<PmIntegration>("/api/v1/orgs/me/pm-integrations", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updatePmIntegration: (
    id: string,
    body: Partial<{
      displayName: string;
      enabled: boolean;
      providerWorkspaceId: string;
      providerDefaultProjectId: string | null;
      credentials: Record<string, unknown>;
    }>,
  ) =>
    customFetch<PmIntegration>(`/api/v1/pm-integrations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deletePmIntegration: (id: string) =>
    customFetch<void>(`/api/v1/pm-integrations/${id}`, { method: "DELETE" }),

  testPmIntegration: (id: string) =>
    customFetch<PmIntegrationTestResult>(`/api/v1/pm-integrations/${id}/test`, { method: "POST" }),

  // --- AI launches: each returns { jobId } you can pass to useAiJob -------
  parseSummary: (projectId: string) =>
    customFetch<{ jobId: string }>(`/api/v1/projects/${projectId}/parse-summary`, {
      method: "POST",
    }),
  parseMatrix: (projectId: string) =>
    customFetch<{ jobId: string }>(`/api/v1/projects/${projectId}/parse-matrix`, {
      method: "POST",
    }),
  generateResearchBrief: (projectId: string) =>
    customFetch<{ jobId: string }>(`/api/v1/projects/${projectId}/research-brief`, {
      method: "POST",
    }),
  generateStrategyBrief: (projectId: string) =>
    customFetch<{ jobId: string }>(`/api/v1/projects/${projectId}/strategy-brief`, {
      method: "POST",
    }),
  generateDraftForMatrixItem: (itemId: string) =>
    customFetch<{ jobId: string }>(`/api/v1/matrix/${itemId}/generate-draft`, { method: "POST" }),
  improveSection: (
    sectionId: string,
    body: {
      instruction: "tighten" | "expand" | "more_evidence" | "plain_english" | "custom";
      customInstruction?: string;
    },
  ) =>
    customFetch<{ jobId: string }>(`/api/v1/sections/${sectionId}/improve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  runQa: (projectId: string) =>
    customFetch<{ jobId: string; runId: string }>(`/api/v1/projects/${projectId}/qa/run`, {
      method: "POST",
    }),

  // --- Exports -----------------------------------------------------------
  exportProject: (projectId: string, format: "docx" | "pdf" = "docx") =>
    customFetch<{ fileId: string; downloadUrl: string }>(`/api/v1/projects/${projectId}/export`, {
      method: "POST",
      body: JSON.stringify({ format }),
    }),

  // --- Teamwork sync -----------------------------------------------------
  syncToTeamwork: (projectId: string) =>
    customFetch<{
      createdCount: number;
      skippedCount: number;
      errors?: Array<{ itemId: string; message: string }>;
    }>(`/api/v1/projects/${projectId}/teamwork-sync`, { method: "POST" }),

  // --- Notes / Updates -------------------------------------------------------
  listNotes: (projectId: string) => customFetch<Note[]>(`/api/v1/projects/${projectId}/notes`),

  createNote: (
    projectId: string,
    body: {
      kind: NoteKind;
      body: string;
      fileId?: string | null;
      feedToAi?: boolean;
    },
  ) =>
    customFetch<Note>(`/api/v1/projects/${projectId}/notes`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateNote: (
    noteId: string,
    body: {
      kind?: NoteKind;
      body?: string;
      feedToAi?: boolean;
    },
  ) =>
    customFetch<Note>(`/api/v1/notes/${noteId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteNote: (noteId: string) =>
    customFetch<void>(`/api/v1/notes/${noteId}`, { method: "DELETE" }),

  updateFileTag: (fileId: string, tag: FileTag) =>
    customFetch<ProjectFile>(`/api/v1/files/${fileId}`, {
      method: "PATCH",
      body: JSON.stringify({ tag }),
    }),

  getFile: (fileId: string) => customFetch<ProjectFile>(`/api/v1/files/${fileId}`),

  /** Presigned GET URL for downloading or previewing a stored file. */
  getFileDownloadUrl: (fileId: string) =>
    customFetch<{ downloadUrl: string; expiresAt: string }>(`/api/v1/files/${fileId}/download-url`),

  getOrgContext: () => customFetch<OrgContextResponse>("/api/v1/me/org-context"),

  updateOrgContext: (body: Partial<OrgContext>) =>
    customFetch<OrgContext>("/api/v1/me/org-context", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  getClientContext: (projectId: string) =>
    customFetch<ClientContextResponse>(`/api/v1/projects/${projectId}/client-context`),

  updateClientContext: (projectId: string, body: Partial<ClientContext>) =>
    customFetch<ClientContext>(`/api/v1/projects/${projectId}/client-context`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  createClientLink: (projectId: string, url: string) =>
    customFetch<ClientLink>(`/api/v1/projects/${projectId}/links`, {
      method: "POST",
      body: JSON.stringify({ url }),
    }),

  deleteClientLink: (linkId: string) =>
    customFetch<void>(`/api/v1/links/${linkId}`, { method: "DELETE" }),

  getStrategy: (projectId: string) =>
    customFetch<Strategy>(`/api/v1/projects/${projectId}/strategy`),

  updateStrategy: (projectId: string, body: Partial<Strategy>) =>
    customFetch<Strategy>(`/api/v1/projects/${projectId}/strategy`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  generateStrategyJob: (projectId: string) =>
    customFetch<{ jobId: string }>(`/api/v1/projects/${projectId}/strategy/generate`, {
      method: "POST",
    }),
};
